import type { JSONContent } from "@tiptap/core";

type ParagraphNodeT = JSONContent & { type: "paragraph" };

const isTextNodeEmpty = (node: JSONContent): boolean => {
  if (node.type !== "text") {
    return false;
  }

  const value = node.text ?? "";
  return value.trim().length === 0;
};

const isMeaningfulNode = (node: JSONContent): boolean => {
  if (!node.type) {
    return false;
  }

  if (node.type === "text") {
    return !isTextNodeEmpty(node);
  }

  // Treat hardBreak nodes as meaningful so they are preserved when already present.
  if (node.type === "hardBreak") {
    return true;
  }

  // Nested content: recursively check for meaningful children.
  if (Array.isArray(node.content)) {
    return node.content.some(isMeaningfulNode);
  }

  return true;
};

const isEmptyParagraph = (
  node: JSONContent | undefined
): node is ParagraphNodeT => {
  if (!node || node.type !== "paragraph") {
    return false;
  }

  if (!Array.isArray(node.content) || node.content.length === 0) {
    return true;
  }

  return !node.content.some(isMeaningfulNode);
};

const convertParagraphToHardBreak = (node: ParagraphNodeT): ParagraphNodeT => {
  const content = [{ type: "hardBreak" } satisfies JSONContent];
  return {
    ...node,
    content,
  };
};

/**
 * Ensures a doc node always contains at least one block node, so the editor
 * is never initialized/synced with a truly empty document
 * ({ type: "doc", content: [] }), which is invalid against the ProseMirror
 * schema (doc expects block+).
 *
 * Non-doc nodes and null/undefined pass through unchanged.
 */
export function ensureNonEmptyDoc(
  content: JSONContent | null | undefined
): JSONContent | null | undefined {
  if (!content || content.type !== "doc") {
    return content;
  }

  if (Array.isArray(content.content) && content.content.length > 0) {
    return content;
  }

  return { ...content, content: [{ type: "paragraph" }] };
}

/**
 * Converts editor JSON into plain JSON objects.
 *
 * ProseMirror builds node/mark `attrs` with `Object.create(null)` and
 * `Node.toJSON()` returns those objects by reference. React Flight refuses to
 * serialize null-prototype objects as plain data when they are passed to a
 * Server Action and replaces them with a temporary reference (a function proxy
 * on the server), which then fails Zod validation with
 * "expected record, received function". A JSON round-trip restores
 * `Object.prototype` on every nested object.
 */
export function toPlainJsonContent(content: JSONContent): JSONContent {
  return JSON.parse(JSON.stringify(content)) as JSONContent;
}

export function sanitizeTiptapContent(
  content: JSONContent | null
): JSONContent | null {
  if (!content || content.type !== "doc") {
    return content;
  }

  const nodes = Array.isArray(content.content) ? content.content : [];
  if (nodes.length === 0) {
    return { ...content, content: [] };
  }

  let startIndex = 0;
  while (startIndex < nodes.length && isEmptyParagraph(nodes[startIndex])) {
    startIndex += 1;
  }

  let endIndex = nodes.length - 1;
  while (endIndex >= startIndex && isEmptyParagraph(nodes[endIndex])) {
    endIndex -= 1;
  }

  if (startIndex > endIndex) {
    return { ...content, content: [] };
  }

  const trimmedNodes = nodes
    .slice(startIndex, endIndex + 1)
    .map((node) =>
      isEmptyParagraph(node) ? convertParagraphToHardBreak(node) : node
    );

  if (
    trimmedNodes.length === nodes.length &&
    trimmedNodes.every((node, index) => node === nodes[index])
  ) {
    return content;
  }

  return { ...content, content: trimmedNodes };
}
